import { API_URL } from '../../../../constants/urls.constants';
import type {
  CommonResponse,
  FailedJobResponse,
  FailedJobsParams,
  GetQueryArgsType,
} from '../../../../types/global.types';
import { appServiceApi } from '../../../apis/app-service.api';
import { APP_API_TAGS } from '../../../app.constants';
import { userLoggedOut } from '../../auth/auth.slice';
import type {
  EmployeeAttachmentListResponse,
  UploadEmployeeAttachmentsBody,
  AllDepartmentResponse,
  AllDesignationResponse,
  AllEmployeeParams,
  AllEmployeeResponse,
  AllFailedEmployeeData,
  DepartmentData,
  DepartmentParams,
  DesignationData,
  DesignationParams,
  EmployeeData,
  EmployeeStates,
  FailedEmployeeListParams,
  FailedEmployeeListResponse,
  FailedImportJobsV2Response,
  FailedImportRecordsV2Params,
  FailedImportRecordsV2Response,
  LastActionData,
  UserSpecificLogParams,
  UserSpecificLogResponse,
  EmployeeExportParams,
} from './emp-dashboard.type';

type DashboardParams = {
  start_date?: string;
  end_date?: string;
  organization_id?: string;
  department_id?: string;
};

type LastActionParams = {
  organizationId?: string;
};

type AttendanceTrendData = {
  total_employees: number;
  daily_stats: {
    date: string;
    present_count: number;
    on_leave_count: number;
    absent_count: number;
    no_data: number;
  }[];
};

const {
  EMPLOYEES,
  EMPLOYEE_DOCUMENTS,
  DEPARTMENTS,
  DESIGNATIONS,
  ATTENDANCES,
  AUDIT_LOGS,
  ALL_FAILED_EMPLOYEE,
} = APP_API_TAGS || {};

export const empDashboardApi = appServiceApi.injectEndpoints({
  endpoints: (builder) => ({
    // ---------------------------------------------------------------
    // Employee attachments (JTI step 6)
    // ---------------------------------------------------------------

    getEmployeeAttachments: builder.query<
      EmployeeAttachmentListResponse,
      { employeeId: string }
    >({
      query: ({ employeeId }) => ({
        url: `${API_URL.employees}/${employeeId}/attachments`,
        method: 'GET',
      }),
      providesTags: (_r, _e, arg) => [
        { type: EMPLOYEE_DOCUMENTS, id: arg.employeeId },
      ],
    }),

    /** The files themselves travel here; one call can carry several. */
    uploadEmployeeAttachments: builder.mutation<
      any,
      UploadEmployeeAttachmentsBody
    >({
      query: ({ employeeId, documentName, documentType, files }) => {
        const formData = new FormData();
        formData.append('documentName', documentName);
        formData.append('documentType', documentType);
        files.forEach((file) => formData.append('files', file));

        return {
          url: `${API_URL.employees}/${employeeId}/attachments/upload`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (res, _e, arg) =>
        res ? [{ type: EMPLOYEE_DOCUMENTS, id: arg.employeeId }] : [],
    }),

    deleteEmployeeAttachment: builder.mutation<
      any,
      { employeeId: string; id: string }
    >({
      query: ({ employeeId, id }) => ({
        url: `${API_URL.employees}/${employeeId}/attachments/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (res, _e, arg) =>
        res ? [{ type: EMPLOYEE_DOCUMENTS, id: arg.employeeId }] : [],
    }),

    // ------ Start: API Endpoints for Employee Management ------
    createEmployee: builder.mutation({
      query: (data) => ({
        url: API_URL.employees,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (res) => (res ? [EMPLOYEES] : []),
    }),

    updateEmployee: builder.mutation({
      query: ({ data, id }) => ({
        url: `${API_URL.employees}/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (res, __, arg) => {
        return res ? [{ type: EMPLOYEES, id: arg.id }, EMPLOYEES] : [];
      },
    }),

    getAllEmployees: builder.query<
      CommonResponse<AllEmployeeResponse>,
      GetQueryArgsType<AllEmployeeParams>
    >({
      query: (queryArgs) => ({
        url: API_URL.employees,
        ...queryArgs,
      }),
      providesTags: [EMPLOYEES],
    }),

    getEmployeeDetails: builder.query<
      CommonResponse<{ data: EmployeeData }>,
      string
    >({
      query: (id) => ({ url: `${API_URL.employees}/${id}` }),
      providesTags: (_, __, id) => [{ type: EMPLOYEES, id }],
    }),

    getMyProfile: builder.query<CommonResponse<{ data: EmployeeData }>, string>(
      {
        query: (id) => ({
          url: `${API_URL.employees}/get-me/${id}`,
        }),
        async onQueryStarted(_, { queryFulfilled, dispatch }) {
          try {
            const { data } = await queryFulfilled;
            const profile = data?.data?.data;
            if (profile) {
              const { setProfileInfo } = await import('../../auth/auth.slice');
              dispatch(setProfileInfo(profile));
            }
          } catch (err) {
            dispatch(userLoggedOut());
          }
        },
        providesTags: (_, __, id) => [
          { type: EMPLOYEES, id: `my-profile-${id}` },
        ],
      },
    ),
    getFailedEmployeeJobs: builder.query<
      CommonResponse<FailedJobResponse>,
      GetQueryArgsType<FailedJobsParams>
    >({
      query: (queryArgs) => ({
        url: `${API_URL.employees}/failed-employees/jobs`,
        ...queryArgs,
      }),
      providesTags: [EMPLOYEES],
    }),
    getFailedEmployeeList: builder.query<
      CommonResponse<FailedEmployeeListResponse>,
      GetQueryArgsType<FailedEmployeeListParams>
    >({
      query: (queryArgs) => ({
        url: `${API_URL.employees}/failed-employees/list`,
        ...queryArgs,
      }),
      providesTags: [EMPLOYEES],
    }),
    decidePhotoPermission: builder.mutation({
      query: (data) => ({
        url: `${API_URL.employees}/photo-permission/decide`,
        method: 'POST',
        body: data,
      }),

      invalidatesTags: (res, __, arg) =>
        res ? [{ type: EMPLOYEES, id: arg.userId }, EMPLOYEES] : [],
    }),
    uploadEmployeeProfilePhoto: builder.mutation({
      query: ({ data, id }) => ({
        url: `${API_URL.employees}/upload-photo/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (res, __, arg) =>
        res ? [{ type: EMPLOYEES, id: arg.id }, EMPLOYEES] : [],
    }),
    // ------ End: API Endpoints for Employee Management ------

    // ------ Start: API Endpoints for Department Management ------
    createDepartment: builder.mutation({
      query: (data) => {
        return {
          url: API_URL.departments,
          method: 'POST',
          body: data,
        };
      },
      invalidatesTags: (res) => (res ? [DEPARTMENTS] : []),
    }),

    updateDepartment: builder.mutation({
      query: ({ data, id }) => {
        return {
          url: `${API_URL.departments}/${id}`,
          method: 'PATCH',
          body: data,
        };
      },
      invalidatesTags: (res, __, arg) => {
        return res ? [{ type: DEPARTMENTS, id: arg.id }, DEPARTMENTS] : [];
      },
    }),

    deleteDepartment: builder.mutation({
      query: ({ id }) => ({
        url: `${API_URL.departments}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (res, __, arg) => {
        return res ? [{ type: DEPARTMENTS, id: arg.id }, DEPARTMENTS] : [];
      },
    }),

    getAllDepartments: builder.query<
      CommonResponse<AllDepartmentResponse>,
      GetQueryArgsType<DepartmentParams>
    >({
      query: (queryArgs) => ({
        url: API_URL.departments,
        ...queryArgs,
      }),
      providesTags: [DEPARTMENTS],
    }),

    getDepartmentDetails: builder.query<CommonResponse<DepartmentData>, string>(
      {
        query: (id) => ({
          url: `${API_URL.departments}/${id}`,
        }),
        providesTags: (_, __, id) => [{ type: DEPARTMENTS, id }],
      },
    ),
    // ------ End: API Endpoints for Department Management ------

    // ------ Start: API Endpoints for Designation Management ------
    createDesignation: builder.mutation({
      query: (data) => {
        return {
          url: API_URL.designations,
          method: 'POST',
          body: data,
        };
      },
      invalidatesTags: (res) => (res ? [DESIGNATIONS] : []),
    }),

    updateDesignation: builder.mutation({
      query: ({ data, id }) => {
        return {
          url: `${API_URL.designations}/${id}`,
          method: 'PATCH',
          body: data,
        };
      },
      invalidatesTags: (res, __, arg) => {
        return res ? [{ type: DESIGNATIONS, id: arg.id }, DESIGNATIONS] : [];
      },
    }),

    deleteDesignation: builder.mutation({
      query: ({ id }) => ({
        url: `${API_URL.designations}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (res, __, arg) => {
        return res ? [{ type: DESIGNATIONS, id: arg.id }, DESIGNATIONS] : [];
      },
    }),

    getAllDesignations: builder.query<
      CommonResponse<AllDesignationResponse>,
      GetQueryArgsType<DesignationParams>
    >({
      query: (queryArgs) => ({
        url: API_URL.designations,
        ...queryArgs,
      }),
      providesTags: [DESIGNATIONS],
    }),

    getDesignationDetails: builder.query<
      CommonResponse<DesignationData>,
      number | string
    >({
      query: (id) => ({
        url: `${API_URL.designations}/${id}`,
      }),
      providesTags: (_, __, id) => [{ type: DESIGNATIONS, id }],
    }),
    // ------ End: API Endpoints for Designation Management ------

    // ------ Start: Dashboard API ------
    getDailyStates: builder.query<
      CommonResponse<{ message: string; data: AttendanceTrendData }>,
      DashboardParams
    >({
      query: (params) => ({
        url: `${API_URL.dashboard}/attendance/daily-stats`,
        params,
      }),
      providesTags: () => [ATTENDANCES],
    }),
    getEmployeeStats: builder.query<
      CommonResponse<{ data: EmployeeStates }>,
      DashboardParams
    >({
      query: (params) => ({
        url: `${API_URL.dashboard}/employee-stats`,
        params,
      }),
      providesTags: () => [EMPLOYEES],
    }),
    getLastActionStats: builder.query<
      CommonResponse<{ count: number; data: LastActionData[] }>,
      LastActionParams
    >({
      query: (params) => ({
        url: `${API_URL.dashboard}/dashboard-last-action-stats`,
        params: {
          organizationId: params.organizationId,
        },
      }),
      providesTags: () => [ATTENDANCES],
    }),

    // Bulk Employee Upload
    createBulkEmployees: builder.mutation<
      CommonResponse<any>,
      {
        organization_id: string;
        tenant_id: string;
        employees: any[];
      }
    >({
      query: (data) => ({
        url: `${API_URL.employees}/bulk-import`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [EMPLOYEES, ALL_FAILED_EMPLOYEE],
    }),

    // Bulk Employee Error List
    getAllFailedEmployee: builder.query<
      AllFailedEmployeeData,
      { params: { page: number; limit: number } }
    >({
      query: (queryArgs) => ({
        url: `${API_URL.employees}/failed-employees/list`,
        ...queryArgs,
      }),
      providesTags: [ALL_FAILED_EMPLOYEE],
    }),

    getUserSpecificLogs: builder.query<
      CommonResponse<UserSpecificLogResponse>,
      GetQueryArgsType<UserSpecificLogParams>
    >({
      query: (queryArgs) => ({
        url: `${API_URL.auditLogs}/user-specific-logs`,
        ...queryArgs,
      }),
      providesTags: [AUDIT_LOGS],
    }),

    // ------ Start: API Endpoints for Bulk Employee Import V2 ------
    // GET: every employee matching the directory filters, as an Excel workbook
    exportEmployees: builder.query<string, EmployeeExportParams>({
      query: (params) => ({
        url: `${API_URL.employees}/export`,
        params,
        cache: 'no-cache',
        responseHandler: async (response: Response) => {
          const blob = await response.blob();
          return window.URL.createObjectURL(blob);
        },
      }),
      keepUnusedDataFor: 0,
    }),

    downloadEmployeeExcel: builder.query<string, void>({
      query: () => ({
        url: `${API_URL.employees}/v2/bulk-import/template`,
        cache: 'no-cache',
        responseHandler: async (response: any) => {
          const blob = await response.blob();
          return window.URL.createObjectURL(blob);
        },
      }),
    }),

    uploadEmployeeExcel: builder.mutation<
      CommonResponse<any>,
      FormData
    >({
      query: (formData) => ({
        url: `${API_URL.employees}/v2/bulk-import/upload`,
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: (res) =>
        res ? [EMPLOYEES, ALL_FAILED_EMPLOYEE] : [],
    }),

    getFailedEmployeeImportJobs: builder.query<
      CommonResponse<FailedImportJobsV2Response>,
      GetQueryArgsType<FailedJobsParams>
    >({
      query: (queryArgs) => ({
        url: `${API_URL.employees}/v2/bulk-import/failed/jobs`,
        ...queryArgs,
      }),
      providesTags: [ALL_FAILED_EMPLOYEE],
    }),

    getFailedEmployeeImportRecords: builder.query<
      CommonResponse<FailedImportRecordsV2Response>,
      GetQueryArgsType<FailedImportRecordsV2Params>
    >({
      query: (queryArgs) => ({
        url: `${API_URL.employees}/v2/bulk-import/failed/list`,
        ...queryArgs,
      }),
      providesTags: [ALL_FAILED_EMPLOYEE],
    }),
    // ------ End: API Endpoints for Bulk Employee Import V2 ------

    // End: API Endpoints for Dashboard
  }),
});

export const {
  // Employee attachments
  useGetEmployeeAttachmentsQuery,
  useUploadEmployeeAttachmentsMutation,
  useDeleteEmployeeAttachmentMutation,
  // API Hooks for Employee
  useCreateEmployeeMutation,
  useUpdateEmployeeMutation,
  useGetAllEmployeesQuery,
  useGetEmployeeDetailsQuery,
  useGetMyProfileQuery,
  useLazyGetMyProfileQuery,
  useGetAllFailedEmployeeQuery,
  useGetFailedEmployeeJobsQuery,
  useGetFailedEmployeeListQuery,
  useDecidePhotoPermissionMutation,
  useUploadEmployeeProfilePhotoMutation,

  // API Hooks for Department
  useCreateDepartmentMutation,
  useUpdateDepartmentMutation,
  useDeleteDepartmentMutation,
  useGetAllDepartmentsQuery,
  useGetDepartmentDetailsQuery,

  // API Hooks for Designation
  useCreateDesignationMutation,
  useUpdateDesignationMutation,
  useDeleteDesignationMutation,
  useGetAllDesignationsQuery,
  useGetDesignationDetailsQuery,

  // API Hooks for Dashboard
  useGetDailyStatesQuery,
  useGetEmployeeStatsQuery,
  useGetLastActionStatsQuery,
  useCreateBulkEmployeesMutation,
  useGetUserSpecificLogsQuery,

  // API Hooks for Bulk Employee Import V2
  useLazyDownloadEmployeeExcelQuery,
  useLazyExportEmployeesQuery,
  useUploadEmployeeExcelMutation,
  useGetFailedEmployeeImportJobsQuery,
  useGetFailedEmployeeImportRecordsQuery,
} = empDashboardApi;
